import { useState, useEffect } from "react";

// Function to detect iPad devices
function isIPadDevice() {
  if (typeof window === "undefined") return false;
  
  // Method 1: Detect via navigator.userAgent (for older iPadOS)
  const userAgent = navigator.userAgent.toLowerCase();
  const isOlderIPad = /ipad/.test(userAgent);
  
  // Method 2: iPadOS 13+ disguises as MacOS, need to detect touch points
  const isMacWithTouch = /macintosh/.test(userAgent) && navigator.maxTouchPoints > 1;
  
  // Method 3: Judge by screen aspect ratio and resolution
  const isTabletRatio = window.innerWidth / window.innerHeight < 1.3 && 
                         window.innerWidth > 600 && 
                         window.innerWidth < 1366;
  
  return isOlderIPad || isMacWithTouch || isTabletRatio;
}

// Detect if it's a tablet device
function isTabletDevice() {
  if (typeof window === "undefined") return false;
  
  // First check if it's an iPad
  if (isIPadDevice()) return true;
  
  const width = window.innerWidth;
  const height = window.innerHeight;
  
  // Check if it's an Android tablet
  const userAgent = navigator.userAgent.toLowerCase();
  const isAndroid = /android/.test(userAgent);
  
  // Tablet device characteristics:
  // 1. Width usually between 600px and 1366px
  // 2. Aspect ratio usually less than 1.3 (more square-like)
  // 3. Android device and not a phone
  // const isTabletSize = width >= 600 && width <= 1366;
  // const isTabletRatio = width / height < 1.3;
  const isNotPhone = !(/mobile/.test(userAgent)) || width > 800;
  
  return (isAndroid && isNotPhone);
}

function getDeviceType() {
  if (typeof window === "undefined") return "desktop";

  const userAgent = navigator.userAgent.toLowerCase();

  // First determine if it's a tablet
  if (isTabletDevice()) return "tablet";

  // Check if it's a phone (including landscape)
  const isMobileUA = /iphone|android.*mobile|windows phone|ipod|blackberry|bb10|mini|mobile|touch/.test(userAgent);

  if (isMobileUA) return "mobile";

  const width = window.innerWidth;

  // Width fallback
  if (width <= 480) return "mobile";
  if (width <= 1280) return "tablet";
  return "desktop";
}

export function useDeviceType() {
  // Initialize as null to prevent SSR and client-side rendering mismatch
  const [device, setDevice] = useState<string | null>(null);

  useEffect(() => {
    // Execute device detection only on client side
    setDevice(getDeviceType());
    
    function handleResize() {
      setDevice(getDeviceType());
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Return null or default value during server-side rendering
  return device || "desktop";
}

// Export a hook specifically for iPad detection
export function useIsIPad() {
  const [isIPad, setIsIPad] = useState(false);
  
  useEffect(() => {
    setIsIPad(isIPadDevice());
    
    function handleResize() {
      setIsIPad(isIPadDevice());
    }
    
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  
  return isIPad;
}

export function useSafeAreaInsetBottom(minInset = 24): number {
  const [bottomInset, setBottomInset] = useState(minInset);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const isAndroid = /Android/i.test(navigator.userAgent);

    const computeInset = () => {
      let inset = 0;

      const div = document.createElement('div');
      div.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        height: env(safe-area-inset-bottom);
        height: constant(safe-area-inset-bottom);
        visibility: hidden;
        pointer-events: none;
      `;
      document.body.appendChild(div);
      const cssValue = parseFloat(getComputedStyle(div).height || '0');
      document.body.removeChild(div);

      if (cssValue > 0) {
        inset = cssValue;
      } else if (window.visualViewport) {
        const diff = window.innerHeight - window.visualViewport.height - window.visualViewport.offsetTop;
        if (diff > 0) inset = diff;
      }

      // If Android, add extra 70px
      if (isAndroid) {
        inset += 70;
      }

      // Minimum guarantee value
      setBottomInset(Math.max(Math.round(inset), minInset));
    };

    computeInset();
    window.visualViewport?.addEventListener('resize', computeInset);
    window.addEventListener('orientationchange', computeInset);

    return () => {
      window.visualViewport?.removeEventListener('resize', computeInset);
      window.removeEventListener('orientationchange', computeInset);
    };
  }, [minInset]);

  return bottomInset;
}

// Page visibility Hook - used to detect if user switches to other tabs
export function usePageVisibility() {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleVisibilityChange = () => {
      const newVisibility = !document.hidden;
      console.log('Visibility changed:', newVisibility, 'document.hidden:', document.hidden);
      setIsVisible(newVisibility);
    };

    // Initialize state
    setIsVisible(!document.hidden);

    // Only listen to visibilitychange event, which is specifically for tab switching detection
    // Don't listen to focus/blur events as they trigger in many other situations
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return isVisible;
}
